
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Award, Star, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useStreakTracking } from '@/hooks/use-streak-tracking';

interface StreakDisplayProps {
  className?: string;
  queuedCount?: number; // Add the queuedCount prop
}

const StreakIcon = ({ 
  count, 
  icon: Icon, 
  color,
  description,
  animate = true
}: { 
  count: number; 
  icon: React.ElementType;
  color: string;
  description: string;
  animate?: boolean;
}) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative flex items-center">
            <motion.div
              className="relative"
              whileHover={{ scale: 1.2 }}
              initial={animate ? { scale: 0.8, opacity: 0 } : false}
              animate={animate ? { scale: 1, opacity: 1 } : false}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Icon className={cn("h-5 w-5", color)} />
              {count > 0 && (
                <motion.span
                  className="absolute -top-1 -right-2.5 text-xs font-bold bg-black/50 rounded-full h-4 w-4 flex items-center justify-center"
                  initial={animate ? { scale: 0, opacity: 0 } : false}
                  animate={animate ? { scale: 1, opacity: 1 } : false}
                  transition={{ delay: 0.1 }}
                >
                  {count}
                </motion.span>
              )}
            </motion.div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const StreakDisplay = ({ className, queuedCount = 0 }: StreakDisplayProps) => {
  const { user } = useAuth();
  const { streakData } = useStreakTracking();
  const [showAnimation, setShowAnimation] = useState(false);
  
  useEffect(() => {
    // Show animation when component mounts
    setShowAnimation(true);
    
    // Reset animation state when unmounting
    return () => setShowAnimation(false);
  }, []);
  
  if (!user) return null;
  
  return (
    <motion.div 
      className={cn("flex items-center gap-3", className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence>
        {streakData.postStreak > 0 && (
          <StreakIcon 
            count={streakData.postStreak} 
            icon={Flame}
            color="text-orange-400 hover:text-orange-300"
            description={`${streakData.postStreak} day posting streak! Keep it up!`}
            animate={showAnimation}
          />
        )}
        
        {streakData.likeStreak > 0 && (
          <StreakIcon 
            count={streakData.likeStreak} 
            icon={Star}
            color="text-yellow-400 hover:text-yellow-300"
            description={`${streakData.likeStreak} day engagement streak! You're on fire!`}
            animate={showAnimation}
          />
        )}
        
        {(streakData.bestPostStreak >= 7 || streakData.bestLikeStreak >= 7) && (
          <StreakIcon 
            count={Math.max(streakData.bestPostStreak, streakData.bestLikeStreak)} 
            icon={Award}
            color="text-purple-400 hover:text-purple-300"
            description={`Achievement unlocked: ${Math.max(streakData.bestPostStreak, streakData.bestLikeStreak)} day best streak!`}
            animate={showAnimation}
          />
        )}
        
        {/* Queue indicator - now using the prop instead of mock hook */}
        {queuedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-1 px-1.5 py-0.5 bg-black/20 backdrop-blur-sm rounded-full text-xs"
          >
            <Clock className="h-3 w-3 text-gray-300" />
            <span className="text-gray-300">{queuedCount} queued</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default StreakDisplay;
